// How a time bucket reads to a human. Pure and UTC throughout.
//
// The shared primitives default to `formatBucketTime` (`@bi/shared`
// chart-format.ts), built for live per-minute ops charts. It prints every
// bucket as a LOCAL clock reading, so on a per-day chart every bucket shows the
// same string ("03:00 AM" in UTC+3), and readers in different zones see
// different labels for one bucket.
//
// So the grain decides the FORMAT (a day bucket reads as a day, never a clock),
// and every reading is UTC, matching the warehouse (`service/bi/core/compiler.py`
// truncates and bounds in UTC) and the range control's "(UTC)" label.
//
// Build a Date only via `Date.UTC` from regex-read digits, never
// `new Date(label)`: its zoneless-string handling is the trap above.

/** The ISO 8601 grains `service/bi/core/compiler.py`'s `_GRAIN_SQL` can compile,
 *  finest to coarsest. `ChartResult.applied_time_grain` carries one of these
 *  (or null, for a result with no time bucket column at all). */
export const GRAIN_ORDER = ["PT1M", "PT1H", "P1D", "P1W", "P1M", "P3M", "P1Y"] as const;

export type TimeGrain = (typeof GRAIN_ORDER)[number];

/** How many seconds one bucket of `grain` covers. The month-ish spans are
 *  nominal, matching the backend ladder's own approximation — used only to
 *  extend a drag-zoom's upper bound to the END of its last bucket, never to
 *  compute a query boundary. */
const GRAIN_SECONDS: Record<TimeGrain, number> = {
  PT1M: 60,
  PT1H: 3_600,
  P1D: 86_400,
  P1W: 604_800,
  P1M: 2_592_000,
  P3M: 7_776_000,
  P1Y: 31_536_000,
};

export function isTimeGrain(value: string | null | undefined): value is TimeGrain {
  return (
    value !== null && value !== undefined && (GRAIN_ORDER as readonly string[]).includes(value)
  );
}

/** True for a grain whose buckets are shorter than a calendar day — the ones
 *  a date-only filter bound cannot express. Mirrors the backend's own
 *  `_SUB_DAY_GRAINS`. */
export function isSubDayGrain(grain: string | null | undefined): boolean {
  return grain === "PT1M" || grain === "PT1H";
}

export function grainSeconds(grain: TimeGrain): number {
  return GRAIN_SECONDS[grain];
}

/** The shapes a BigQuery `DATE`/`DATETIME`/`TIMESTAMP` column serializes to
 *  over JSON, the same family `lib/time-brush.ts` accepts. Must accept the
 *  numeric offset a `TIMESTAMP` really emits (`'2026-08-09T00:00:00+00:00'`),
 *  or labels silently fall back to raw. */
const BUCKET_MATCH =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;

export interface BucketParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** True iff the label carried a time-of-day component at all. */
  hasTime: boolean;
}

/** A bucket label -> its UTC calendar parts, or `null` for a label that isn't
 *  a date at all (an ordinary categorical x-axis: `"US"`, `"stage_won"`).
 *  Refuses rather than guessing, for the same reason `time-brush.ts` does:
 *  `Date`'s own parser accepts `"2024"` and `"Jan"` as valid dates, so
 *  delegating to it would silently render a category as a moment in time. */
export function parseBucket(label: string): BucketParts | null {
  const m = BUCKET_MATCH.exec(label);
  if (!m) return null;
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: Number(m[4] ?? "0"),
    minute: Number(m[5] ?? "0"),
    second: Number(m[6] ?? "0"),
    hasTime: m[4] !== undefined,
  };
}

function toUtcDate(parts: BucketParts): Date {
  return new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second),
  );
}

/** The grain to format by when the wire didn't say — a result cached before
 *  `applied_time_grain` existed (3-hour TTL).
 *
 *  Read off the labels themselves, coarsest reading that fits: every bucket
 *  landing on midnight means these are days (or wider, which formats the same
 *  way at this precision), every one landing on the hour means hours, anything
 *  else means minutes. Coarse is the safe direction to be wrong in — showing
 *  "Aug 9" for an hourly bucket loses precision the reader can still recover
 *  from the tooltip's neighbours, whereas showing a clock reading for a daily
 *  bucket is the identical-label defect this module exists to fix. */
export function inferGrainFromBuckets(buckets: readonly string[]): TimeGrain | null {
  let sawDate = false;
  let sawNonMidnight = false;
  let sawNonHour = false;
  for (const label of buckets) {
    const parts = parseBucket(label);
    if (!parts) return null; // a categorical axis: not a time grain at all
    sawDate = true;
    if (parts.hour !== 0 || parts.minute !== 0 || parts.second !== 0) sawNonMidnight = true;
    if (parts.minute !== 0 || parts.second !== 0) sawNonHour = true;
  }
  if (!sawDate) return null;
  if (sawNonHour) return "PT1M";
  if (sawNonMidnight) return "PT1H";
  return "P1D";
}

/** The grain to format this result's buckets by: what the compiler says it
 *  used, else what the labels themselves imply. */
export function resolveGrain(
  appliedTimeGrain: string | null | undefined,
  buckets: readonly string[],
): TimeGrain | null {
  if (isTimeGrain(appliedTimeGrain)) return appliedTimeGrain;
  return inferGrainFromBuckets(buckets);
}

const UTC = "UTC";
const MONTH_DAY = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: UTC,
});
const MONTH_DAY_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: UTC,
});
const MONTH_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
  timeZone: UTC,
});
const CLOCK = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: UTC,
});

/** A bucket label as a reader should see it, at the precision the data was
 *  actually bucketed to.
 *
 *  `grain` null (or a label that isn't a date) returns the label VERBATIM —
 *  an ordinary categorical x-axis is already the string the reader wants, and
 *  a fabricated date over `stage_won` would be worse than no formatting.
 *
 *  The year appears only when the bucket falls outside the current UTC year,
 *  so an ordinary recent window stays short ("Aug 9") and a historical one
 *  stays unambiguous ("Aug 9, 2025"). `now` is injectable so this is testable
 *  without freezing the clock.
 */
export function formatBucketLabel(
  label: string,
  grain: TimeGrain | null,
  now: Date = new Date(),
): string {
  if (grain === null) return label;
  const parts = parseBucket(label);
  if (!parts) return label;
  const d = toUtcDate(parts);
  const sameYear = parts.year === now.getUTCFullYear();
  switch (grain) {
    case "PT1M":
    case "PT1H": {
      const day = sameYear ? MONTH_DAY.format(d) : MONTH_DAY_YEAR.format(d);
      return `${day}, ${CLOCK.format(d)}`;
    }
    case "P1D":
    case "P1W":
      return sameYear ? MONTH_DAY.format(d) : MONTH_DAY_YEAR.format(d);
    case "P1M":
    case "P3M":
      return MONTH_YEAR.format(d);
    case "P1Y":
      return String(parts.year);
  }
}

/** A `BucketFormatter` bound to one grain, for handing to a chart primitive's
 *  `formatBucket` prop (which is called per bucket, with no grain of its
 *  own). */
export function bucketFormatterFor(grain: TimeGrain | null): (bucket: string) => string {
  return (bucket: string) => formatBucketLabel(bucket, grain);
}

/** The chart primitives' right-hand axis label: the LAST bucket, formatted.
 *
 *  They default that corner to the literal word "now", which is true for the
 *  rolling live ops charts they were built for and a false claim on every
 *  chart here — a reader looking at "Dec 2025 to Jan 2026" was told the right
 *  edge was the present moment.
 *
 *  Returns `undefined` (keep the primitive's own default) when there is no
 *  bucket to name, and an EMPTY string for a single-bucket chart: both axis
 *  corners would otherwise print the same date, which reads as a range from a
 *  day to itself. The left corner already names it.
 */
export function axisEndLabelFor(
  buckets: readonly string[],
  grain: TimeGrain | null,
): string | undefined {
  if (buckets.length === 0) return undefined;
  if (buckets.length === 1) return "";
  const last = buckets[buckets.length - 1];
  if (last === undefined) return undefined;
  // A categorical axis (no grain) names its last category. `undefined` would
  // keep the primitive's default, the word "now".
  return grain === null ? last : formatBucketLabel(last, grain);
}

const GRAIN_NOUN: Record<TimeGrain, string> = {
  PT1M: "minute",
  PT1H: "hour",
  P1D: "day",
  P1W: "week",
  P1M: "month",
  P3M: "quarter",
  P1Y: "year",
};

/** "hour", "day", … — the bucket width in a word, for a reader-facing note. */
export function grainNoun(grain: TimeGrain): string {
  return GRAIN_NOUN[grain];
}

/** The note for a time series that came back with exactly ONE bucket.
 *
 *  `chart-card.tsx` already draws a single-bucket timeseries as a BAR (one
 *  point on a line reads as a broken tile). This note says WHY there is only
 *  one: time-bucketed charts read `*_daily` tables
 *  (`SELECT DATE(occurred_at) AS day … GROUP BY day`), so the time of day is
 *  gone upstream and the compiler refuses to step below a `DATE` floor. A
 *  finer grain cannot fix it. A caveat, not a withhold: the value is correct.
 */
export function singleBucketNote(bucketCount: number, grain: TimeGrain | null): string | null {
  if (bucketCount !== 1 || grain === null) return null;
  const noun = grainNoun(grain);
  return `Only one ${noun} falls in the window you picked, so this is a single value rather than a trend. This chart's data is aggregated per ${noun}, so a shorter window cannot show more detail. Widen the range to compare across ${noun}s.`;
}

/** The note to show when the compiled grain differs from the one the chart is
 *  authored (and usually TITLED) after, else `null`.
 *
 *  This is the caveat half of `frontend/bi/CLAUDE.md`'s caveat-vs-withhold
 *  rule, and it is required rather than optional: the numbers drawn are
 *  correct, but a tile titled "per Day" drawing hourly buckets states a scope
 *  it isn't answering at. One sentence repairs that completely. Withholding
 *  would be the wrong call here — the hourly view is exactly what the reader
 *  asked for by narrowing the window.
 */
export function grainChangeNote(
  authored: string | null | undefined,
  applied: string | null | undefined,
): string | null {
  if (!isTimeGrain(authored) || !isTimeGrain(applied) || authored === applied) return null;
  const finer = GRAIN_ORDER.indexOf(applied) < GRAIN_ORDER.indexOf(authored);
  return finer
    ? `Bucketed by ${grainNoun(applied)} to fit the window you picked, not by ${grainNoun(authored)} as this chart is authored.`
    : `Bucketed by ${grainNoun(applied)} because the window is too wide to draw one bar per ${grainNoun(authored)}.`;
}
